import React, { useState } from 'react';
import { Toast, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { faPlus, faPencil } from '@fortawesome/pro-light-svg-icons';
import { createDataTable } from '@/components/DataTable';
import { useAsyncStateWithIsLoading } from '@/utils/hooks';
import { deactivateMacGyverUser, getMacGyverUsers, MacGyverUser } from '@/services/macgyverUsers';
import MacGyverUserModal from './MacGyverUserModal';
import MacGyverUsersBulkAddModal from './MacGyverUsersBulkAddModal';
import { AlertBanner, Button, ButtonBar, ConfirmButton, SlideToggle } from '@ygtechnologies/rubus-ui';

const DataTable = createDataTable<MacGyverUser>();

const MacGyverUsersTable: React.FC<{}> = () => {
    const [tableState, setTableState] = useState(DataTable.getInitialState('email'));
    const [data, , reload, isLoading] = useAsyncStateWithIsLoading(() => getMacGyverUsers(), [], null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editUser, setEditUser] = useState<MacGyverUser | undefined>(undefined);
    const [isDeactivating, setIsDeactivating] = useState(false);
    const [showInactive, setShowInactive] = useState(false);

    if (!data) {
        return <LoadingIndicator />;
    }

    // Deactivated users stay in the table so their row keeps denying access, but they're clutter in
    // day-to-day use — hide them unless asked for.
    const inactiveCount = data.filter((user) => !user.isActive).length;
    const visibleUsers = showInactive ? data : data.filter((user) => user.isActive);

    const openEdit = (user: MacGyverUser) => {
        setEditUser(user);
        setIsEditModalOpen(true);
    };

    const handleDeactivate = async (user: MacGyverUser) => {
        setIsDeactivating(true);
        try {
            await deactivateMacGyverUser(user.id);
            Toast.success('User deactivated');
            await reload();
        } catch (e) {
            let msg: string | null = null;
            try {
                msg = (await (e as Response).json())?.error ?? null;
            } catch {
                /* non-JSON response */
            }
            Toast.error(msg ?? 'Something went wrong');
        } finally {
            setIsDeactivating(false);
        }
    };

    return (
        <div>
            <AlertBanner type="info">
                Bootstrap admins are managed via configuration and shown here as read-only (always Admin). Access changes
                take effect immediately.
            </AlertBanner>
            {inactiveCount > 0 && (
                <SlideToggle checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}>
                    {inactiveCount === 1 ? 'Show 1 inactive user' : `Show ${inactiveCount} inactive users`}
                </SlideToggle>
            )}
            <DataTable
                hasPagination={false}
                isLoading={isLoading}
                data={visibleUsers}
                onStateChange={setTableState}
                state={tableState}
                totalItemCount={visibleUsers.length}
                toolbarActions={
                    <Button size="sm" variant="primary" onClick={() => setIsAddModalOpen(true)} icon={faPlus}>
                        Add users
                    </Button>
                }
                className="table style-3">
                <DataTable.Column
                    header="Email"
                    field="email"
                    renderer={({ value, row }) =>
                        row.isBootstrapAdmin ? (
                            <>
                                {value} <em>(bootstrap admin)</em>
                            </>
                        ) : (
                            value
                        )
                    }
                />
                <DataTable.Column header="Access level" field="accessLevel" renderer={({ value }) => value} />
                <DataTable.Column header="Active" field="isActive" renderer={({ value }) => (value ? 'Yes' : 'No')} />
                <DataTable.Column
                    header="Actions"
                    field="id"
                    renderer={({ row }) =>
                        row.isBootstrapAdmin ? (
                            <em>Managed via config</em>
                        ) : (
                            <ButtonBar margins="none" noWrap>
                                <Button size="xs" variant="tertiary" icon={faPencil} onClick={() => openEdit(row)}>
                                    Edit
                                </Button>
                                {row.isActive && (
                                    <ConfirmButton
                                        size="xs"
                                        dialogTitle="Deactivate user?"
                                        dialogBody={`Deactivate ${row.email}? They will lose access to MacGyver.`}
                                        onConfirm={() => handleDeactivate(row)}
                                        variant="danger"
                                        confirmButton={{ variant: 'danger' }}
                                        loading={isDeactivating}>
                                        Deactivate
                                    </ConfirmButton>
                                )}
                            </ButtonBar>
                        )
                    }
                />
            </DataTable>

            <MacGyverUsersBulkAddModal
                isOpen={isAddModalOpen}
                toggle={() => setIsAddModalOpen(false)}
                onSave={reload}
            />

            {editUser && (
                <MacGyverUserModal
                    isOpen={isEditModalOpen}
                    toggle={() => setIsEditModalOpen(false)}
                    onSave={reload}
                    user={editUser}
                />
            )}
        </div>
    );
};

export default MacGyverUsersTable;
